import { useEffect, useMemo, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type Transaction } from '../db/database'
import { writeLocal } from '../db/sync'
import { formatAmount, parseAmountBounded } from '../lib/money'
import { MAX_NOM, MAX_NOTE, messageMontant } from '../lib/limites'
import { today } from '../lib/dates'
import { Icon, type IconName } from './Icon'
import './AddTransactionSheet.css'
import { newId } from '../lib/uuid'

/**
 * Saisie d'une opération.
 *
 * Le montant d'abord, en gros, puis le sens, puis la catégorie : c'est l'ordre
 * dans lequel on pense une dépense, pas l'ordre des colonnes en base. Pour un
 * public peu habitué aux applications, chaque champ demandé avant le montant
 * est une occasion d'abandonner.
 *
 * L'enregistrement n'attend JAMAIS le réseau (règle d'architecture) : writeLocal
 * écrit dans Dexie puis rend la main, la synchronisation part derrière.
 *
 * La même feuille SERT À MODIFIER une opération existante. Deux écrans jumeaux
 * dériveraient l'un de l'autre au premier champ ajouté, et corriger une saisie
 * se pense exactement comme la faire.
 */
export function AddTransactionSheet(
  { walletId, onClose, editing, onDelete }: {
    walletId: string
    onClose: () => void
    /** L'opération à corriger. Absente = on en crée une. */
    editing?: Transaction
    /** Retirer l'opération ouverte. La confirmation est à l'appelant. */
    onDelete?: (transaction: Transaction, label: string) => void
  },
) {
  // Groupé comme partout ailleurs — « 90 000 » et non « 90000 ». `parseAmount`
  // retolère les espaces à la relecture.
  const [rawAmount, setRawAmount] = useState(
    editing ? formatAmount(editing.amount) : '')
  const [direction, setDirection] = useState<'income' | 'expense'>(
    editing?.direction ?? 'expense')
  /*
   * Le sens est FIGÉ à la correction.
   *
   * Retourner une dépense en revenu ne corrige rien : ça déplace le montant
   * d'un côté à l'autre du solde, soit un écart du double, sur une ligne dont
   * l'utilisateur voulait seulement rectifier le chiffre. Une opération saisie
   * dans le mauvais sens se supprime et se ressaisit — elle ne se retourne pas.
   */
  const senseFige = editing !== undefined
  const [chosen, setChosen] = useState<string | null>(editing?.category_id ?? null)
  const [title, setTitle] = useState(editing?.title ?? '')
  const [note, setNote] = useState(editing?.note ?? '')
  const amountField = useRef<HTMLInputElement>(null)

  const categories = useLiveQuery(
    () => db.categories.where({ wallet_id: walletId }).toArray(),
    [walletId],
    [],
  )

  const visible = useMemo(
    () => categories.filter((c) => !c.deleted_at && c.direction === direction)
                    .sort((a, b) => a.position - b.position),
    [categories, direction],
  )

  useEffect(() => { amountField.current?.focus() }, [])

  /*
   * « Autres » est choisie d'office.
   *
   * Une opération sans catégorie n'est pas une opération mieux décrite : c'est
   * une ligne qui échappe au budget et qui ne se retrouve plus dans
   * l'historique. Le fourre-tout assumé vaut mieux que le vide — et il reste un
   * clic pour choisir autre chose.
   *
   * Dérivé pendant le rendu, et non posé dans un effet : les catégories
   * arrivent de Dexie en asynchrone, et un effet ferait un second rendu pour
   * corriger le premier. Le choix explicite gagne tant qu'il reste valide —
   * changer de sens le rend caduc, et le fourre-tout du nouveau sens reprend.
   */
  const fourreTout = visible.find((c) => c.icon === 'other') ?? visible[visible.length - 1]
  const categoryId = chosen && visible.some((c) => c.id === chosen)
    ? chosen
    : fourreTout?.id ?? null

  // Changer de sens invalide la catégorie choisie : une dépense « Salaire »
  // n'aurait aucun sens. Remis à zéro DANS le gestionnaire plutôt que dans un
  // effet — un effet provoquerait un second rendu pour corriger le premier,
  // alors que l'information est déjà connue au moment du clic.
  function chooseDirection(next: 'income' | 'expense') {
    setDirection(next)
    setChosen(null)
  }

  const amount = parseAmountBounded(rawAmount)
  // Le message DIT pourquoi, là où le bouton grisé laissait deviner : « pas un
  // nombre » et « trop grand » n'appellent pas la même correction.
  const problemeMontant = messageMontant(rawAmount)
  const canSave = amount !== null && amount > 0

  async function save() {
    if (!canSave) return
    await writeLocal('transactions', {
      id: editing?.id ?? newId(),
      wallet_id: walletId,
      category_id: categoryId,
      entered_by_account_id: null,
      amount,
      direction,
      // La date d'une opération corrigée ne bouge pas : on rectifie un montant
      // ou un libellé, on ne déplace pas la dépense dans le temps.
      operation_date: editing?.operation_date ?? today(),
      title: title.trim() || null,
      note: note.trim() || null,
    })

    /*
     * Une journée confirmée porte DEUX chiffres : la transaction écrite et le
     * `planned_amount` de l'allocation. Corriger l'une sans l'autre laisse
     * l'avancement du plan afficher un montant que plus rien ne justifie — le
     * genre d'écart qui ne plante pas et se découvre des semaines plus tard.
     */
    if (editing) {
      const allocation = (await db.budget_allocations
        .where({ wallet_id: walletId }).toArray())
        .find((a) => a.transaction_id === editing.id && !a.deleted_at)
      if (allocation && allocation.planned_amount !== amount) {
        await writeLocal('budget_allocations', { ...allocation, planned_amount: amount })
      }
    }
    onClose()
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label={editing ? 'Modifier une opération' : 'Ajouter une opération'}>
      <div className="sheet">
        <header className="sheet-head">
          <h2>{editing ? 'Modifier' : 'Ajouter'}</h2>
          {/* La poubelle vit ICI et pas parmi les champs : c'est une sortie de
              l'écran, pas une valeur à régler. */}
          {editing && onDelete && (
            <button type="button" className="sheet-delete"
                    onClick={() => onDelete(
                      editing,
                      // Le même arbitrage que dans la liste : titre, puis
                      // catégorie, puis note.
                      editing.title
                        ?? categories.find((c) => c.id === editing.category_id)?.name
                        ?? editing.note
                        ?? 'Opération')}
                    aria-label="Supprimer cette opération">
              <Icon name="trash" size={18} />
            </button>
          )}
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Fermer">
            <Icon name="close" size={20} />
          </button>
        </header>

        <label className="amount-field">
          <span className="visually-hidden">Montant en Ariary</span>
          <input
            ref={amountField}
            className="tabular"
            // `inputMode` et non `type="number"` : le clavier numérique
            // s'ouvre, mais sans les flèches ni le défilement accidentel qui
            // change une valeur sans qu'on s'en aperçoive.
            inputMode="numeric"
            autoComplete="off"
            placeholder="0"
            value={rawAmount}
            onChange={(e) => setRawAmount(e.target.value)}
          />
          <span className="amount-unit">Ar</span>
        </label>
        {problemeMontant
          ? <p className="amount-erreur" role="alert">{problemeMontant}</p>
          : amount !== null && amount > 0 && (
            <p className="amount-echo tabular">{formatAmount(amount)} Ar</p>
          )}

        <div className="direction" role="group" aria-label="Sens de l'opération">
          <button
            type="button"
            className={direction === 'expense' ? 'is-selected is-expense' : ''}
            aria-pressed={direction === 'expense'}
            disabled={senseFige && direction !== 'expense'}
            onClick={() => chooseDirection('expense')}
          >
            <Icon name="arrowUp" size={18} /> Dépense
          </button>
          <button
            type="button"
            className={direction === 'income' ? 'is-selected is-income' : ''}
            aria-pressed={direction === 'income'}
            disabled={senseFige && direction !== 'income'}
            onClick={() => chooseDirection('income')}
          >
            <Icon name="arrowDown" size={18} /> Revenu
          </button>
        </div>

        <div className="categories" role="group" aria-label="Catégorie">
          {visible.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`category ${categoryId === category.id ? 'is-selected' : ''}`}
              aria-pressed={categoryId === category.id}
              onClick={() => setChosen(category.id)}
            >
              <Icon name={(category.icon as IconName) ?? 'other'} size={20} />
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        <label className="note-field">
          <span className="visually-hidden">Titre</span>
          <input
            type="text"
            placeholder="Titre (facultatif)"
            value={title}
            maxLength={MAX_NOM}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <label className="note-field">
          <span className="visually-hidden">Description</span>
          {/* Un `textarea` et non un `input` : une description tient rarement
              sur une ligne, et un champ qui défile à l'horizontale cache ce
              qu'on vient d'écrire. Il grandit avec le texte, sans dépasser. */}
          <textarea
            placeholder="Description (facultatif)"
            value={note}
            rows={2}
            maxLength={MAX_NOTE}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <button type="button" className="save" disabled={!canSave} onClick={save}>
          Enregistrer
        </button>
      </div>
    </div>
  )
}
